import React, { useState } from 'react';
import styled from 'styled-components';
import adminBg from '../Assets/bg/adminbg.jpg';
import Loader from '../Components/Loader';
import useGetAllUser from '../Hooks/Admin/useGetAllUser';
import useDeleteUser from '../Hooks/Admin/useDeleteUser';
import DeleteConfirmationModal from '../Components/DeleteConfirmationModal';
import AddAccount from '../Admin/AddAccount';

const ManagementAdmin = ({ darkMode, isSidebarExpanded }) => {
  const { users, isLoading, error, refetch } = useGetAllUser();
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [panelMode, setPanelMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAllData, setShowAllData] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { deleteUser, loading: deleteLoading, error: deleteError, successMessage } = useDeleteUser();

  const handleClose = () => {
    setShowRightPanel(false);
    setPanelMode('create');
    setSelectedUser(null);
    console.log('Form closed');
  };

  const handleDeleteClick = (item) => {
    setConfirmDelete(item);
  };

  const handleEditClick = (item) => {
    setPanelMode('edit');
    setSelectedUser(item);
    setShowRightPanel(true);
    console.log('Edit user clicked:', item);
  };

  // Filter users based on search query
  const filteredUsers = users.filter((user) =>
    Object.values(user).some((val) =>
      (val?.toString()?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    )
  );

  // For "Show All Data" functionality, limit to 10 users unless showAllData is true
  const displayedUsers = showAllData ? filteredUsers : filteredUsers.slice(0, 10);
  const shouldShowButton = filteredUsers.length > 10;

  return (
    <SlidingPanel darkMode={darkMode} show={true} isExpanded={isSidebarExpanded}>
      <PanelsWrapper>
        <Container darkMode={darkMode} isSidebarExpanded={isSidebarExpanded} showRightPanel={showRightPanel}>
          <GradientOverlay />
          <HeaderContainer>
            <HeaderLeft>
              <HeaderText darkMode={darkMode}>
                <h2>Account Staff Management</h2>
                <p>Manage system accounts</p>
              </HeaderText>
            </HeaderLeft>
            <RightControls>
              <StyledButton
                onClick={() => {
                  refetch();
                  setPanelMode('create');
                  setSelectedUser(null);
                  setShowRightPanel(true);
                }}
              >
                Create Account
              </StyledButton>
            </RightControls>
          </HeaderContainer>
          <TableContainer>
            {isLoading && (
              <LoaderContainer>
                <Loader />
              </LoaderContainer>
            )}
            {error && (
              <ErrorMessage>
                Error: {error}
                <StyledButton onClick={refetch}>Retry</StyledButton>
              </ErrorMessage>
            )}
            {!isLoading && !error && (
              <TableWrapper isExpanded={isSidebarExpanded} id="tableWrapper">
                <div id="scrollContent" style={{ width: '100%', overflowX: 'auto' }}>
                  <Table darkMode={darkMode}>
                    <thead>
                      <tr>
                        <TableHeader darkMode={darkMode}>ACTIONS</TableHeader>
                        <TableHeader darkMode={darkMode}>CODE</TableHeader>
                        <TableHeader darkMode={darkMode}>NAME</TableHeader>
                        <TableHeader darkMode={darkMode}>ROLE</TableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedUsers.length === 0 ? (
                        <tr>
                          <TableData darkMode={darkMode} colSpan="4" style={{ textAlign: 'center' }}>
                            No users found.
                          </TableData>
                        </tr>
                      ) : (
                        displayedUsers.map((user, index) => (
                          <TableRow
                            key={user.id || index}
                            darkMode={darkMode}
                          >
                            <TableData
                              darkMode={darkMode}
                              data-label="Actions"
                              style={{ textAlign: 'center', width: '130px', padding: '8px 0' }}
                            >
                              <ButtonGroup>
                                <StyledButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(user);
                                  }}
                                  title="Edit User"
                                  aria-label={`Edit ${user.name || 'user'}`}
                                >
                                  ✏️
                                </StyledButton>
                                <StyledButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(user);
                                  }}
                                  title="Delete User"
                                  aria-label={`Delete ${user.name || 'user'}`}
                                >
                                  ❌
                                </StyledButton>
                              </ButtonGroup>
                            </TableData>
                            <TableData
                              darkMode={darkMode}
                              data-label="Code"
                              style={{ textAlign: 'center', width: '100px' }}
                            >
                              {user.code}
                            </TableData>
                            <TableData
                              darkMode={darkMode}
                              data-label="Name"
                              style={{ textAlign: 'left', width: '200px' }}
                            >
                              {user.name || 'Unknown'}
                            </TableData>
                            <TableData
                              darkMode={darkMode}
                              data-label="Role"
                              style={{ textAlign: 'center', width: '100px' }}
                            >
                              {user.role || 'N/A'}
                            </TableData>
                          </TableRow>
                        ))
                      )}
                    </tbody>
                  </Table>
                  {shouldShowButton && (
                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                      <ShowAllButton onClick={() => setShowAllData(!showAllData)}>
                        {showAllData ? 'Show Less Data' : 'Show All Data'}
                      </ShowAllButton>
                    </div>
                  )}
                </div>
              </TableWrapper>
            )}
            <div
              style={{
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
              className="hide-scrollbar"
              id="bottomScroll"
            >
              <div style={{ width: '480px', height: '1px' }} />
            </div>
            {confirmDelete && (
              <DeleteConfirmationModal
                itemName={confirmDelete.name || 'User'}
                onCancel={() => setConfirmDelete(null)}
                onConfirm={async () => {
                  try {
                    await deleteUser(confirmDelete.id); // Ensure deleteNIS is a function
                    refetch();
                    setConfirmDelete(null);
                  } catch (err) {
                    console.error('Delete error:', err);
                  }
                }}
              />
            )}
          </TableContainer>
        </Container>
        <RightPanel darkMode={darkMode} show={showRightPanel}>
          <AddAccount
            darkMode={darkMode}
            onClose={handleClose}
            onRefresh={refetch}
            mode={panelMode}
            editUser={selectedUser}
          />
        </RightPanel>
      </PanelsWrapper>
    </SlidingPanel>
  );
};

export default ManagementAdmin;

// Styled Components
const SIDEBAR_WIDTH_EXPANDED = '180px';
const SIDEBAR_WIDTH_COLLAPSED = '60px';
const PanelsWrapper = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;

  @media (max-width: 1280px) {
    height: auto;
    min-height: 100vh;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Container = styled.div`
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#e7f2ec')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  color: ${({ darkMode }) => (darkMode ? '#757575' : '#333')};
  padding: 80px clamp(16px, 4vw, 36px) clamp(24px, 5vw, 40px);
  flex: ${({ showRightPanel }) => (showRightPanel ? '0 1 60%' : '1 1 100%')};
  max-width: ${({ showRightPanel }) => (showRightPanel ? '60%' : '100%')};
  min-height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  margin-left: ${({ isSidebarExpanded }) => (isSidebarExpanded ? '6px' : '4px')};
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  transition: margin-left 0.3s ease, width 0.3s ease;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  @media (max-width: 1280px) {
    max-height: none;
  }

  @media (max-width: 1100px) {
    width: ${({ showRightPanel }) => (showRightPanel ? '58%' : '100%')};
    margin-left: clamp(0px, 5vw, 80px);
  }

  @media (max-width: 1024px) {
    flex: 1 1 100%;
    max-width: 100%;
    margin-left: 0;
    min-height: auto;
    padding-top: clamp(72px, 12vw, 92px);
    overflow-y: visible;
  }

  @media (max-width: 768px) {
    padding: clamp(60px, 15vw, 88px) clamp(12px, 6vw, 24px) clamp(30px, 10vw, 48px);
    box-shadow: none;
    background-attachment: fixed;
  }

  @media (max-width: 480px) {
    padding: 64px 12px 32px;
  }
`;

const SlidingPanel = styled.div`
  position: fixed;
  top: 0;
  left: ${({ isExpanded }) => (isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED)};
  width: ${({ isExpanded }) => (isExpanded ? `calc(100% - ${SIDEBAR_WIDTH_EXPANDED})` : `calc(100% - ${SIDEBAR_WIDTH_COLLAPSED})`)};
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#e7f2ec')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  transform: translateX(100%);
  transition: transform 0.5s ease, left 0.3s ease-in-out, width 0.3s ease-in-out, padding 0.3s ease-in-out;
  z-index: 2;
  padding: 20px;
  box-sizing: border-box;

  ${({ show }) => show && `transform: translateX(0);`}

  @media (max-width: 1280px) {
    padding: clamp(16px, 3vw, 24px);
  }

  @media (max-width: 768px) {
    background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#e7f2ec')};
    box-shadow: none;
    padding-left: clamp(12px, 4vw, 24px);
    padding-right: clamp(12px, 4vw, 24px);
  }
`;

const LoaderContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 clamp(12px, 4vw, 40px);
  gap: clamp(12px, 4vw, 24px);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HeaderText = styled.div`
  margin-bottom: 6px;
  margin-top: clamp(-20px, -6vw, -36px);

  h2 {
    margin: 0;
    font-size: clamp(20px, 3.2vw, 26px);
    color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  }

  p {
    margin: 0;
    font-size: clamp(14px, 2.5vw, 18px);
    color: ${({ darkMode }) => (darkMode ? '#cccccc' : '#555')};
  }
`;

const RightControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  padding-right: clamp(12px, 4vw, 40px);

  @media (max-width: 768px) {
    justify-content: flex-start;
    padding-right: 0;
  }
`;

const StyledButton = styled.button`
  margin: 0 2px;
  padding: 8px 14px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;
  font-weight: 600;

  &:hover {
    background-color: #ddd;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;
  justify-content: center;
`;

const ShowAllButton = styled.button`
  margin: 20px auto;
  display: block;
  padding: 8px 16px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #ddd;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  height: 75vh;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 10px;
  position: relative;
  background: #e7f2ec;
  box-shadow: 0 4px 16px rgba(33, 70, 47, 0.12);
  border-radius: 18px;
  border: 1.5px solid #d7e8de;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  #scrollContent {
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  #scrollContent::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 1024px) {
    height: auto;
    max-height: 60vh;
  }

  @media (max-width: 768px) {
    max-height: none;
    overflow-y: visible;
    background-color: ${({ darkMode }) => (darkMode ? 'rgba(42, 48, 42, 0.85)' : '#e7f2ec')};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 480px;
  position: relative;
  background: #e7f2ec;
  box-shadow: 0 2px 12px rgba(33, 70, 47, 0.1);
  border-radius: 18px;
  border: 1.5px solid #d7e8de;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};

  @media (max-width: 768px) {
    min-width: 420px;
  }

  @media (max-width: 640px) {
    min-width: 100%;
  }
`;

const TableHeader = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
  color: ${({ darkMode }) => (darkMode ? '#333' : '#fff')};
  padding: clamp(8px, 2vw, 12px);
  font-size: clamp(12px, 2.4vw, 14px);
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;

  &:first-child {
    position: sticky;
    left: 0;
    z-index: 3;
    background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
    width: 130px;
  }
  &:nth-child(2) {
    width: 100px; /* CODE column */
  }
  &:nth-child(3) {
    width: 200px; /* NAME column */
  }
  &:nth-child(4) {
    width: 100px; /* ROLE column */
  }

  @media (max-width: 640px) {
    display: none;
  }
`;

const TableData = styled.td`
  padding: clamp(6px, 1.8vw, 10px);
  font-size: clamp(11px, 2.2vw, 13px);
  border: 1px solid #ddd;
  color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
  transition: background-color 0.3s ease, color 0.3s ease;

  &:first-child {
    position: sticky;
    left: 0;
    background-color: ${({ darkMode }) => (darkMode ? 'rgb(37, 112, 46)' : '#e7f2ec')};
    z-index: 1;
    width: 130px;
    text-align: center;
  }
  &:nth-child(2) {
    width: 100px;
    text-align: center;
  }
  &:nth-child(3) {
    width: 200px;
    text-align: left;
  }
  &:nth-child(4) {
    width: 100px;
    text-align: center;
  }

  @media (max-width: 640px) {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    width: 100% !important;
    text-align: right !important;
    padding: 12px;

    &:first-child,
    &:nth-child(2),
    &:nth-child(3),
    &:nth-child(4) {
      position: relative;
      left: auto;
      background: ${({ darkMode }) => (darkMode ? 'rgba(37,112,46,0.45)' : '#e7f2ec')};
      z-index: auto;
    }

    &::before {
      content: attr(data-label);
      font-weight: 600;
      text-transform: uppercase;
      font-size: 11px;
      color: ${({ darkMode }) => (darkMode ? '#cde9ce' : '#2e7d32')};
      letter-spacing: 0.6px;
    }
  }
`;

const TableRow = styled.tr`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#e7f2ec')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};

  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? 'rgb(37, 112, 46)' : '#f1f1f1')};
    color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
  }

  &:hover td {
    background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
    color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  }

  &:nth-child(even) {
    background-color: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#deede3')};
  }

  @media (max-width: 640px) {
    display: block;
    margin-bottom: 12px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);

    &:hover td {
      background-image: none;
    }
  }
`;

const RightPanel = styled.div`
  position: fixed;
  top: 0;
  right: ${({ show }) => (show ? '0' : '-45%')};
  width: 43%;
  height: 100%;
  background: rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.17);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 24px 0 0 24px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  z-index: 5;
  padding: clamp(12px, 3vw, 20px);
  box-sizing: border-box;
  transition: right 0.3s ease-in-out;

  @media (max-width: 1280px) {
    width: 45%;
  }

  @media (max-width: 1024px) {
    width: 100%;
    right: ${({ show }) => (show ? '0' : '-100%')};
    backdrop-filter: blur(12px);
    position: fixed;
    max-width: none;
  }

  @media (max-width: 768px) {
    height: 100vh;
    padding: clamp(16px, 5vw, 28px);
  }

  @media (max-width: 480px) {
    padding: 20px 12px;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  pointer-events: none;
  z-index: 0;
`;

const TableContainer = styled.div`
  margin-top: 0;
  padding: 0 clamp(12px, 4vw, 28px);

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: red;
  font-size: 16px;
`;